import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { ArrowRight, Sparkles, Palette, Frame } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredImages, setFeaturedImages] = useState([]);

  useEffect(() => {
    fetchFeaturedImages();
  }, []);

  const fetchFeaturedImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      // Get first 3 images for featured section
      setFeaturedImages(response.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 border border-copper/30 rotate-45" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 border border-copper/20 rotate-12" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 text-center relative z-10">
          {/* Logo Mark */}
          <div className="w-24 h-24 mx-auto mb-8 animate-fade-in">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <defs>
                <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#B87333" />
                  <stop offset="50%" stopColor="#D8AB4E" />
                  <stop offset="100%" stopColor="#8B4513" />
                </linearGradient>
              </defs>
              <polygon points="50,5 95,85 5,85" fill="none" stroke="url(#heroGradient)" strokeWidth="3" />
              <polygon points="50,25 75,70 25,70" fill="none" stroke="url(#heroGradient)" strokeWidth="2" />
            </svg>
          </div>

          {/* Main Heading */}
          <h1 
            data-testid="hero-title"
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-medium tracking-tight mb-6 animate-fade-in"
            style={{ animationDelay: "0.1s" }}
          >
            <span className="copper-text">KAIROS</span>
          </h1>
          <h2 
            className="font-accent text-xl md:text-2xl tracking-[0.3em] text-gray-400 mb-8 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            COPPER ART
          </h2>

          {/* Tagline */}
          <p 
            data-testid="hero-tagline"
            className="font-body text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in"
            style={{ animationDelay: "0.3s" }}
          >
            Transform your cherished moments into timeless copper masterpieces. 
            Each piece is meticulously handcrafted with precision and passion.
          </p>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: "0.4s" }}
          >
            <Link to="/quote">
              <Button
                data-testid="hero-get-quote-btn"
                className="bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase px-8 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(184,115,51,0.4)] flex items-center gap-2"
              >
                Get Your Quote
                <ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/gallery">
              <Button
                data-testid="hero-view-gallery-btn"
                variant="outline"
                className="border-copper text-copper hover:bg-copper hover:text-white font-body text-sm tracking-widest uppercase px-8 py-6 transition-all duration-300"
              >
                View Gallery
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-copper/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-copper rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 
            data-testid="features-title"
            className="font-heading text-3xl md:text-4xl text-center mb-16"
          >
            <span className="copper-text">The Art</span> of Copper
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div 
              data-testid="feature-handcrafted"
              className="bg-white/[0.02] border border-white/5 p-8 hover:border-copper/30 transition-all duration-500 group"
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center border border-copper/50 group-hover:border-copper transition-colors">
                <Sparkles size={24} className="text-copper" />
              </div>
              <h3 className="font-heading text-xl mb-4 text-white">Handcrafted</h3>
              <p className="font-body text-gray-400 text-sm leading-relaxed">
                Each piece is individually crafted by hand, cutting, flattening, and placing each copper piece with precision.
              </p>
            </div>

            {/* Feature 2 */}
            <div 
              data-testid="feature-unique"
              className="bg-white/[0.02] border border-white/5 p-8 hover:border-copper/30 transition-all duration-500 group"
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center border border-copper/50 group-hover:border-copper transition-colors">
                <Palette size={24} className="text-copper" />
              </div>
              <h3 className="font-heading text-xl mb-4 text-white">Unique Medium</h3>
              <p className="font-body text-gray-400 text-sm leading-relaxed">
                A revolutionary copper mosaic art form that creates stunning visual depth and texture unlike any other.
              </p>
            </div>

            {/* Feature 3 */}
            <div 
              data-testid="feature-custom"
              className="bg-white/[0.02] border border-white/5 p-8 hover:border-copper/30 transition-all duration-500 group"
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center border border-copper/50 group-hover:border-copper transition-colors">
                <Frame size={24} className="text-copper" />
              </div>
              <h3 className="font-heading text-xl mb-4 text-white">Custom Orders</h3>
              <p className="font-body text-gray-400 text-sm leading-relaxed">
                Send your image and receive a personalized quote. Every piece is made to order, tailored to your vision.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work Section */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <h2 
              data-testid="featured-title"
              className="font-heading text-3xl md:text-4xl"
            >
              Featured <span className="copper-text">Work</span>
            </h2>
            <Link to="/gallery">
              <Button
                data-testid="view-all-btn"
                variant="ghost"
                className="text-copper hover:text-copper-light font-body text-sm tracking-widest uppercase flex items-center gap-2"
              >
                View All
                <ArrowRight size={16} />
              </Button>
            </Link>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredImages.map((image, index) => (
              <div
                key={image.id}
                data-testid={`featured-image-${index}`}
                className="relative aspect-square overflow-hidden group cursor-pointer border border-white/5"
              >
                <img
                  src={image.image_url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = `https://images.unsplash.com/photo-1601069151015-0a7fb104503c?w=400&h=400&fit=crop`;
                  }}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                  <div>
                    <p className="text-copper text-xs tracking-widest uppercase mb-1">{image.category}</p>
                    <h3 className="font-heading text-lg text-white">{image.title}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-24 md:py-32 border-t border-white/5 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 
            data-testid="pricing-title"
            className="font-heading text-3xl md:text-4xl mb-6"
          >
            <span className="copper-text">Pricing</span> Starts At
          </h2>
          <p className="font-body text-gray-400 mb-12 max-w-xl mx-auto">
            Prices include frame. Final cost depends on image complexity and size.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-4xl mx-auto">
            <div data-testid="price-5x7" className="border border-white/10 p-6 hover:border-copper/30 transition-colors">
              <p className="text-3xl md:text-4xl font-heading copper-text mb-2">$20</p>
              <p className="text-gray-400 text-sm">5×7"</p>
            </div>
            <div data-testid="price-8x10" className="border border-white/10 p-6 hover:border-copper/30 transition-colors">
              <p className="text-3xl md:text-4xl font-heading copper-text mb-2">$35</p>
              <p className="text-gray-400 text-sm">8×10"</p>
            </div>
            <div data-testid="price-12x16" className="border border-white/10 p-6 hover:border-copper/30 transition-colors">
              <p className="text-3xl md:text-4xl font-heading copper-text mb-2">$50</p>
              <p className="text-gray-400 text-sm">12×16"</p>
            </div>
            <div data-testid="price-portrait" className="border border-white/10 p-6 hover:border-copper/30 transition-colors">
              <p className="text-3xl md:text-4xl font-heading copper-text mb-2">$85+</p>
              <p className="text-gray-400 text-sm">Portraits</p>
            </div>
          </div>

          <Link to="/quote" className="inline-block mt-12">
            <Button
              data-testid="get-exact-quote-btn"
              className="bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase px-8 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(184,115,51,0.4)]"
            >
              Get Your Exact Quote
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 
            data-testid="cta-title"
            className="font-heading text-3xl md:text-5xl mb-6"
          >
            Ready to Create Your <span className="copper-text">Masterpiece</span>?
          </h2>
          <p className="font-body text-gray-400 mb-8 text-lg">
            Upload your image and get an instant quote powered by AI analysis.
          </p>
          <Link to="/quote">
            <Button
              data-testid="final-cta-btn"
              className="bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase px-10 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(184,115,51,0.4)]"
            >
              Start Your Order
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
